import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserCircle, Spade } from "@phosphor-icons/react";
import { fmtBRL, elapsedFrom } from "@/lib/format";

export default function SeatMap({ table, players, onSeat, onUnseat }) {
    const seats = table.seats || [];
    const [dialog, setDialog] = useState(null);
    const [selPlayer, setSelPlayer] = useState("");

    const openSeat = (seat) => { setDialog(seat); setSelPlayer(""); };
    const confirm = async () => {
        if (!selPlayer) { toast.error("Select a player"); return; }
        await onSeat(dialog.seat_number, selPlayer);
        setDialog(null);
    };

    return (
        <div className="felt-surface grain rounded-2xl p-8 border gold-border relative">
            <div className="absolute top-4 left-6 flex items-center gap-2">
                <Spade weight="fill" size={18} className="text-gold" />
                <span className="font-serif text-xl">{table.name}</span>
            </div>
            <div className="absolute top-4 right-6 label-mini">
                {table.game_type} · {fmtBRL(table.small_blind)}/{fmtBRL(table.big_blind)}
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mt-14">
                {seats.map((s) => {
                    const taken = !!s.player_id;
                    return (
                        <div
                            key={s.seat_number}
                            data-testid={`seat-${s.seat_number}`}
                            className={`rounded-lg p-4 h-28 flex flex-col justify-between transition-transform hover:-translate-y-0.5 ${
                                taken ? "seat-taken" : "seat-empty"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="label-mini">Seat {s.seat_number}</span>
                                {taken ? (
                                    <UserCircle size={20} weight="fill" className="text-gold" />
                                ) : (
                                    <UserCircle size={20} className="text-muted-foreground opacity-60" />
                                )}
                            </div>
                            {taken ? (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-base font-medium leading-tight truncate max-w-[140px]">{s.player_name}</div>
                                        <div className="text-xs text-muted-foreground">for {elapsedFrom(s.seated_at)}</div>
                                    </div>
                                    <button data-testid={`btn-unseat-${s.seat_number}`} onClick={() => onUnseat(s.seat_number)} className="text-destructive text-xs hover:underline">Leave</button>
                                </div>
                            ) : (
                                <Button data-testid={`btn-seat-${s.seat_number}`} onClick={() => openSeat(s)} variant="outline" size="sm" className="h-9 text-sm border-gold/30">
                                    Seat player
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>

            <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
                <DialogContent className="bg-card">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Seat at position {dialog?.seat_number}</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label className="label-mini">Player</Label>
                        <Select value={selPlayer} onValueChange={setSelPlayer}>
                            <SelectTrigger data-testid="select-seat-player"><SelectValue placeholder="Pick a player" /></SelectTrigger>
                            <SelectContent>
                                {players.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button data-testid="btn-confirm-seat" onClick={confirm} className="btn-gold">Confirm</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
